
/** @jsxImportSource react */
import React, { useMemo, useState, useEffect } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import {
  Package,
  Search,
  ChevronDown,
  CheckCircle,
  Clock,
  AlertTriangle,
  Loader2,
  RefreshCcw,
  Plus,
  Send,
  X,
  Inbox,
  UserCheck,
} from 'lucide-react'
import { api } from '../services/api'
import { Order, User, UserRole, OrderStatus, PriceCheckRequest } from '../types'
import { useTranslation } from '../i18n'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { toast } from '../components/ui/Toast'
import { useAuth } from '../contexts/auth'


// Price Submissions Popup Card Component
const PriceSubmissionsPopup = ({ order, userMap, staffList, onClose }) => {
    const { t } = useTranslation();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // Re-use logic from OrderDetailsPage PriceCheckManager
    const assignedStaffIds = useMemo(() => new Set(order.priceCheckRequests?.map(pcr => pcr.staffId) || []), [order.priceCheckRequests]);
    const availableStaff = useMemo(() => staffList.filter(s => !assignedStaffIds.has(s.id)), [staffList, assignedStaffIds]);

    const updateOrderMutation = useMutation({
        mutationFn: (updates: Partial<Order>) => api.updateOrder(order.id, updates),
    });

    const handleAddStaff = (newStaffIds: string[]) => {
        const newRequests: PriceCheckRequest[] = newStaffIds.map(staffId => ({
            staffId,
            status: 'pending'
        }));

        const combinedRequests = [...(order.priceCheckRequests || []), ...newRequests];
        
        const updates: { [key: string]: any } = {
            priceCheckRequests: combinedRequests
        };
        for (const staffId of newStaffIds) {
            updates[`priceCheckStaffIds/${staffId}`] = true;
        }

        updateOrderMutation.mutate(updates, {
            onSuccess: () => {
                toast.success(t('staffAddedSuccess'));
                setIsAddModalOpen(false);
                const notificationPromises = newStaffIds.map(staffId => api.sendNotification({
                    userId: staffId,
                    message: '', // FIX: Add empty message to satisfy type
                    messageKey: 'newPriceCheckRequest',
                    messageParams: { orderId: order.orderId },
                    link: `/price-check-list`
                }));
                Promise.all(notificationPromises);
            },
            onError: () => {
                toast.error(t('staffAddedError'));
            }
        });
    };
    
    const AddStaffModal: React.FC<{ onClose: () => void; onAdd: (staffIds: string[]) => void; isAdding: boolean }> = ({ onClose, onAdd, isAdding }) => {
        const [selectedStaff, setSelectedStaff] = useState<Set<string>>(new Set());
        
        const handleToggleStaff = (staffId: string) => {
            const newSet = new Set(selectedStaff);
            if (newSet.has(staffId)) {
                newSet.delete(staffId);
            } else {
                newSet.add(staffId);
            }
            setSelectedStaff(newSet);
        };
        
        return (
            <Modal title={t('selectStaffToAdd')} onClose={onClose}>
                <div className="max-h-60 overflow-y-auto border rounded-lg p-2 space-y-1 mb-4">
                    {availableStaff.length > 0 ? availableStaff.map(staff => (
                        <label key={staff.id} className="flex items-center p-2 rounded-md hover:bg-secondary-50 cursor-pointer">
                            <input type="checkbox" checked={selectedStaff.has(staff.id)} onChange={() => handleToggleStaff(staff.id)} className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500" />
                            <span className="ms-3 text-secondary-700">{staff.name}</span>
                        </label>
                    )) : <p className="p-4 text-center text-secondary-500">{t('noAvailableStaff')}</p>}
                </div>
                <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose}>{t('cancel')}</Button>
                    <Button onClick={() => onAdd(Array.from(selectedStaff))} disabled={isAdding || selectedStaff.size === 0}>
                        {isAdding ? t('adding') : t('addStaff')}
                    </Button>
                </div>
            </Modal>
        );
    };

    if (!order || !order.priceCheckRequests) return null;

    const submittedCount = order.priceCheckRequests?.filter(
        (r) => r.status === 'submitted'
    ).length || 0;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col animate-scale-in">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-secondary-200">
                    <div>
                        <h3 className="text-lg font-semibold text-secondary-900">
                            {t('priceCheckManagement')}
                        </h3>
                        <p className="text-sm text-secondary-500 mt-1">
                            {order.orderId} • {submittedCount}/{order.priceCheckRequests.length} {t('submitted')}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-secondary-100 rounded-lg transition-colors">
                        <X className="h-5 w-5 text-secondary-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="overflow-y-auto p-6 flex-1">
                    <div className="space-y-4">
                        {order.priceCheckRequests.map((req) => (
                            <div key={req.staffId} className="flex items-center p-3 bg-secondary-50 rounded-xl border border-secondary-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-sm font-medium text-primary-600 flex-shrink-0">
                                        {userMap[req.staffId]?.name?.charAt(0) || 'U'}
                                    </div>
                                    <div>
                                        <p className="font-medium text-secondary-900">
                                            {userMap[req.staffId]?.name || 'Unknown Staff'}
                                        </p>
                                        <p className="text-sm text-secondary-500">
                                            {req.status === 'submitted' 
                                              ? `${t('priceSubmitted')}: $${req.price}`
                                              : t('awaitingResponse')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center gap-3 p-6 border-t border-secondary-200 bg-secondary-50">
                    <Button variant="outline" onClick={() => setIsAddModalOpen(true)} className="bg-white">
                        <Plus className="w-4 h-4 me-1" />
                        {t('addStaff')}
                    </Button>
                    <Button onClick={onClose} className="bg-primary-500 hover:bg-primary-600 text-white">
                        {t('close')}
                    </Button>
                </div>

                {isAddModalOpen && (
                    <AddStaffModal 
                        onClose={() => setIsAddModalOpen(false)}
                        onAdd={handleAddStaff}
                        isAdding={updateOrderMutation.isPending}
                    />
                )}
            </div>
        </div>
    );
};


// Staff Assigner Component
const StaffAssigner = ({ staffList, orderId, currentAssigneeId }) => {
  const { t } = useTranslation()
  const [selectedStaffId, setSelectedStaffId] = useState(currentAssigneeId || '')

  const assignMutation = useMutation({
    mutationFn: (staffId: string) =>
      api.updateOrder(orderId, {
        assignedStaffId: staffId,
      }),
    onSuccess: () => {
      toast.success(t('orderAssigned'), {
        icon: '✅',
        style: {
          borderRadius: '10px',
          background: '#F8FAFC',
          color: '#0F172A',
        },
      })
      // No invalidation needed due to real-time listener
    },
    onError: () => {
      toast.error(t('assignmentFailed'), {
        icon: '❌',
        style: {
          borderRadius: '10px',
          background: '#F8FAFC',
          color: '#0F172A',
        },
      })
    },
  })

  const handleAssign = () => {
    if (!selectedStaffId) {
      toast.error(t('selectStaffFirst'), {
        icon: '⚠️',
        style: {
          borderRadius: '10px',
          background: '#F8FAFC',
          color: '#0F172A',
        },
      })
      return
    }
    assignMutation.mutate(selectedStaffId)
  }

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full max-w-xs sm:max-w-none">
      <div className="relative w-full">
        <select
          value={selectedStaffId}
          onChange={(e) => setSelectedStaffId(e.target.value)}
          className="w-full px-4 py-2.5 rounded-xl bg-white border border-secondary-200 text-secondary-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all pe-10 shadow-sm hover:border-primary-300"
        >
          <option value="" disabled>
            {t('selectStaff')}
          </option>
          {staffList.map((staff) => (
            <option key={staff.id} value={staff.id}>
              {staff.name}
            </option>
          ))}
        </select>
        <div className="absolute end-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <ChevronDown className="h-4 w-4 text-secondary-400" />
        </div>
      </div>
      <Button
        onClick={handleAssign}
        disabled={!selectedStaffId || assignMutation.isPending}
        size="sm"
        className="bg-primary-500 hover:bg-primary-600 transition-colors shadow-sm hover:shadow w-full sm:w-auto shrink-0"
      >
        {assignMutation.isPending ? (
          <Loader2 className="h-4 w-4 me-2 animate-spin" />
        ) : (
          <UserCheck className="h-4 w-4 me-2" />
        )}
        {t('assign')}
      </Button>
    </div>
  )
}

// Tab Button Component
const TabButton = ({ label, isActive, onClick, icon, count }) => (
  <button
    onClick={onClick}
    className={`px-4 py-3 font-medium text-sm transition-all rounded-t-lg focus:outline-none flex items-center gap-2 ${
      isActive 
        ? 'border-b-2 border-primary-500 text-primary-600 bg-primary-50/50' 
        : 'border-b-2 border-transparent text-secondary-500 hover:text-primary-500 hover:bg-secondary-50'
    }`}
  >
    {icon}
    {label}
    {count !== undefined && (
      <span
        className={`ms-1.5 px-1.5 py-0.5 text-xs rounded-full ${
          isActive ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-600'
        }`}
      >
        {count}
      </span>
    )}
  </button>
)

// Price Check Modal Component
const PriceCheckModal = ({ order, staffList, onClose, onSend, isSending }) => {
  const { t } = useTranslation()
  const [selectedStaff, setSelectedStaff] = useState(new Set())

  const handleToggleStaff = (staffId) => {
    const newSet = new Set(selectedStaff)
    if (newSet.has(staffId)) {
      newSet.delete(staffId)
    } else {
      newSet.add(staffId)
    }
    setSelectedStaff(newSet)
  }

  const handleSend = () => {
    if (selectedStaff.size === 0) {
      toast.error(
        t('selectStaffFirst'),
        {
          icon: '⚠️',
          style: {
            borderRadius: '10px',
            background: '#F8FAFC',
            color: '#0F172A',
          },
        },
      )
      return
    }
    onSend(Array.from(selectedStaff))
  }

  return (
    <Modal
      title={`${t('sendForPriceCheck')}: ${order.orderId}`}
      onClose={onClose}
    >
      <div className="mb-6 p-4 bg-primary-50 rounded-xl border border-primary-100 text-primary-800 flex items-start">
        <AlertTriangle className="h-5 w-5 me-3 flex-shrink-0 mt-0.5 text-primary-500" />
        <p className="text-sm">
          {t('selectStaffForPriceCheck')}
        </p>
      </div>
      <div className="max-h-60 overflow-y-auto border border-secondary-200 rounded-xl p-3 space-y-2 mb-6">
        {staffList.map((staff) => (
          <label
            key={staff.id}
            className="flex items-center p-3 rounded-xl hover:bg-secondary-50 cursor-pointer transition-colors"
          >
            <input
              type="checkbox"
              checked={selectedStaff.has(staff.id)}
              onChange={() => handleToggleStaff(staff.id)}
              className="h-4 w-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
            />
            <span className="ms-3 text-secondary-700">{staff.name}</span>
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={onClose}
          className="border-secondary-200 hover:bg-secondary-50"
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSend}
          disabled={isSending || selectedStaff.size === 0}
          className="bg-primary-500 hover:bg-primary-600 transition-colors"
        >
          {isSending ? (
            <>
              <Loader2 className="h-4 w-4 me-2 animate-spin" />
              {t('sending')}
            </>
          ) : (
            <>
              <Send className="h-4 w-4 me-2" />
              {t('send')}
            </>
          )}
        </Button>
      </div>
    </Modal>
  )
}

// Status Badge Component
const StatusBadge = ({ status, text }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'bg-primary-100 text-primary-800 border border-primary-200'
      case 'warning':
        return 'bg-secondary-300 text-secondary-800 border border-secondary-400'
      case 'info':
        return 'bg-primary-100 text-primary-800 border border-primary-200'
      case 'pending':
        return 'bg-secondary-100 text-secondary-800 border border-secondary-200'
      default:
        return 'bg-secondary-100 text-secondary-800 border border-secondary-200'
    }
  }

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-3.5 w-3.5" />
      case 'warning':
        return <AlertTriangle className="h-3.5 w-3.5" />
      case 'info':
        return <Package className="h-3.5 w-3.5" />
      case 'pending':
        return <Clock className="h-3.5 w-3.5" />
      default:
        return null
    }
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full ${getStatusColor()}`}
    >
      {getStatusIcon()}
      {text}
    </span>
  )
}

// Main IncomingOrdersPage Component
export const IncomingOrdersPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation()
  const navigate = useNavigate()

  // State
  const [activeTab, setActiveTab] = useState('unassigned')
  const [searchTerm, setSearchTerm] = useState('')
  const [priceCheckOrder, setPriceCheckOrder] = useState(null)
  const [showPricePopup, setShowPricePopup] = useState(null)
  const [orders, setOrders] = useState<Order[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Real-time data fetching
  useEffect(() => {
      if (!user?.id) return;

      if (orders.length === 0) setIsLoading(true);
      
      // Use synchronous listenToOrders with direct user object
      const unsubOrders = api.listenToOrders(user, (newOrders) => {
          setOrders(newOrders);
          setIsLoading(false);
      });

      let unsubUsers: (() => void) | undefined;
      if (user.role === UserRole.Admin || user.role === UserRole.Staff) {
          unsubUsers = api.listenToUsers((newUsers) => {
              setUsers(newUsers);
          });
      }
      
      return () => {
          if (unsubOrders) unsubOrders();
          if (unsubUsers) unsubUsers();
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, user.role]);

  // Derived state
  const staffList = useMemo(
    () => users.filter((user) => user.role === UserRole.Staff),
    [users],
  )

  const userMap = useMemo(
    () =>
      users.reduce((acc, user) => {
        acc[user.id] = user
        return acc
      }, {}),
    [users],
  )

  // Filtered and sorted orders
  const filteredAndSortedOrders = useMemo(() => {
    return orders
      .filter((order) => {
        // Tab filtering
        if (
          activeTab === 'unassigned' &&
          (order.assignedStaffId || order.status === OrderStatus.PriceChecking)
        )
          return false
        if (activeTab === 'assigned' && !order.assignedStaffId) return false
        
        // Search filtering
        const clientName = userMap[order.clientId]?.name || ''
        const lowerSearch = searchTerm.toLowerCase()
        if (!lowerSearch) return true
        return (
          order.orderId.toLowerCase().includes(lowerSearch) ||
          order.productName.toLowerCase().includes(lowerSearch) ||
          clientName.toLowerCase().includes(lowerSearch)
        )
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
  }, [orders, activeTab, searchTerm, userMap])

  // Counts for tabs
  const unassignedCount = useMemo(
    () =>
      orders.filter(
        (o) => !o.assignedStaffId && o.status !== OrderStatus.PriceChecking,
      ).length,
    [orders],
  )

  const assignedCount = useMemo(
    () => orders.filter((o) => o.assignedStaffId).length,
    [orders],
  )

  // Mutations
  const priceCheckMutation = useMutation({
    mutationFn: ({ orderId, staffIds }: { orderId: string, staffIds: string[] }) => 
      api.sendForPriceCheck(orderId, staffIds),
    onSuccess: () => {
      toast.success(
        t('priceCheckSent'),
        {
          icon: '✅',
          style: {
            borderRadius: '10px',
            background: '#F8FAFC',
            color: '#0F172A',
          },
        },
      )
      setPriceCheckOrder(null)
    },
    onError: () =>
      toast.error(
        t('priceCheckFailed'),
        {
          icon: '❌',
          style: {
            borderRadius: '10px',
            background: '#F8FAFC',
            color: '#0F172A',
          },
        },
      ),
  })

  // Rendering helpers
  const renderAssignmentStatus = (order) => {
    if (order.assignedStaffId) {
      return (
        <StaffAssigner
          staffList={staffList}
          orderId={order.id}
          currentAssigneeId={order.assignedStaffId}
        />
      );
    }
    
    if (order.status === OrderStatus.PriceChecking && order.priceCheckRequests) {
      const submittedCount = order.priceCheckRequests.filter(
        (r) => r.status === 'submitted',
      ).length
      
      return (
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowPricePopup(order)}
            className="group relative"
          >
            <StatusBadge
              status="info"
              text={`${submittedCount}/${order.priceCheckRequests.length} ${t('pricesReceived')}`}
            />
          </button>
          <StaffAssigner
            staffList={staffList}
            orderId={order.id}
            currentAssigneeId={order.assignedStaffId}
          />
        </div>
      )
    }
    
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="border-secondary-200 hover:border-primary-200 hover:bg-primary-50 text-secondary-600 hover:text-primary-600 transition-all"
          onClick={() => setPriceCheckOrder(order)}
        >
          <Send className="h-3.5 w-3.5 me-1.5" />
          {t('sendForPriceCheck')}
        </Button>
        <StaffAssigner
          staffList={staffList}
          orderId={order.id}
          currentAssigneeId={order.assignedStaffId}
        />
      </div>
    )
  }

  // Table Skeleton for loading state
  const TableSkeleton = () => (
    <div className="animate-pulse">
      <table className="w-full">
        <thead className="bg-secondary-50">
          <tr>
            <th className="p-3">
              <div className="h-5 w-24 bg-secondary-200 rounded"></div>
            </th>
            <th className="p-3">
              <div className="h-5 w-32 bg-secondary-200 rounded"></div>
            </th>
            <th className="p-3">
              <div className="h-5 w-48 bg-secondary-200 rounded"></div>
            </th>
            <th className="p-3">
              <div className="h-5 w-24 bg-secondary-200 rounded"></div>
            </th>
            <th className="p-3">
              <div className="h-5 w-40 bg-secondary-200 rounded"></div>
            </th>
          </tr>
        </thead>
        <tbody>
          {[...Array(5)].map((_, i) => (
            <tr key={i} className="border-b">
              <td className="p-3">
                <div className="h-5 bg-secondary-200 rounded"></div>
              </td>
              <td className="p-3">
                <div className="h-5 bg-secondary-200 rounded"></div>
              </td>
              <td className="p-3">
                <div className="h-5 bg-secondary-200 rounded"></div>
              </td>
              <td className="p-3">
                <div className="h-5 bg-secondary-200 rounded"></div>
              </td>
              <td className="p-3">
                <div className="h-9 bg-secondary-200 rounded"></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  if (!user || user.role !== UserRole.Admin) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="relative min-h-screen w-full bg-white p-4 md:p-6 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-500/5 rounded-full mix-blend-multiply filter blur-3xl opacity-70 fixed"></div>
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary-500/5 rounded-full mix-blend-multiply filter blur-3xl opacity-70 fixed"></div>
      
      <div className="container mx-auto relative z-10">
        {/* Header with animated entrance */}
        <div className="mb-8 animate-fade-in">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-secondary-900 mb-2 flex items-center">
                <Inbox className="h-8 w-8 me-3 text-primary-500" />
                {t('incomingOrders')}
              </h1>
              <p className="text-secondary-600">
                {t('incomingOrdersDescription')}
              </p>
            </div>
          </div>
        </div>

        {/* Main content card with enhanced styling */}
        <Card className="overflow-hidden mb-8 animate-fade-in border border-secondary-200 shadow-sm hover:shadow-md transition-all">
          {/* Tab navigation */}
          <div className="flex items-center border-b border-secondary-200 overflow-x-auto bg-secondary-50/50">
            <TabButton
              label={t('unassigned')}
              icon={<Clock size={16} />}
              isActive={activeTab === 'unassigned'}
              onClick={() => setActiveTab('unassigned')}
              count={unassignedCount}
            />
            <TabButton
              label={t('assigned')}
              icon={<CheckCircle size={16} />}
              isActive={activeTab === 'assigned'}
              onClick={() => setActiveTab('assigned')}
              count={assignedCount}
            />
            <TabButton
              label={t('all')}
              icon={<Package size={16} />}
              isActive={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
              count={orders.length}
            />
          </div>

          {/* Search and filters */}
          <div className="p-5 border-b border-secondary-200 bg-white">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                  <Search className="h-4 w-4 text-secondary-400" />
                </div>
                <input
                  type="text"
                  placeholder={t('searchIncomingPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full ps-10 pe-4 py-2.5 rounded-xl border border-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>
          </div>
          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <TableSkeleton />
            ) : filteredAndSortedOrders.length > 0 ? (
              <table className="w-full text-sm text-start text-secondary-500">
                <thead className="text-xs text-secondary-700 uppercase bg-secondary-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-start">{t('order')}</th>
                    <th scope="col" className="px-6 py-3 text-start">{t('client')}</th>
                    <th scope="col" className="px-6 py-3 text-start">{t('date')}</th>
                    <th scope="col" className="px-6 py-3 text-start">{t('status')}</th>
                    <th scope="col" className="px-6 py-3 text-start">{t('assignment')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedOrders.map((order) => (
                    <tr key={order.id} className="bg-white border-b hover:bg-secondary-50">
                      <th scope="row" className="px-6 py-4 font-medium text-secondary-900 whitespace-nowrap text-start">
                        <p className="font-bold text-primary-600 cursor-pointer hover:underline" onClick={() => navigate(`/orders/${order.id}`)}>{order.orderId}</p>
                        <p className="text-xs text-secondary-600 line-clamp-1">{order.productName}</p>
                      </th>
                      <td className="px-6 py-4">{userMap[order.clientId]?.name || t('unknown')}</td>
                      <td className="px-6 py-4">{new Date(order.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4">
                        <StatusBadge
                          status={order.status === OrderStatus.Received ? 'pending' : (order.status === OrderStatus.PriceChecking ? 'info' : 'success')}
                          text={t(order.status.toLowerCase())}
                        />
                      </td>
                      <td className="px-6 py-4 min-w-[300px]">
                        {renderAssignmentStatus(order)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-16 px-4 animate-fade-in">
                <div className="w-20 h-20 bg-secondary-100 rounded-full mx-auto flex items-center justify-center mb-6">
                    <Inbox className="w-10 h-10 text-secondary-400" />
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-2">
                    {activeTab === 'unassigned' ? t('allCaughtUp') : t('noOrdersFound')}
                </h3>
                <p className="text-secondary-500 mb-8 max-w-md mx-auto">
                    {searchTerm ? t('noOrdersMatch') : (activeTab === 'unassigned' ? t('noNewOrders') : t('noOrdersInCategory'))}
                </p>
                {searchTerm && (
                    <Button variant="outline" onClick={() => setSearchTerm('')}>
                        <RefreshCcw className="w-4 h-4 me-2" />
                        {t('clearSearch')}
                    </Button>
                )}
              </div>
            )}
          </div>
        </Card>

        {priceCheckOrder && (
            <PriceCheckModal
                order={priceCheckOrder}
                staffList={staffList}
                onClose={() => setPriceCheckOrder(null)}
                onSend={(staffIds) => priceCheckMutation.mutate({ orderId: priceCheckOrder.id, staffIds })}
                isSending={priceCheckMutation.isPending}
            />
        )}
        {showPricePopup && (
            <PriceSubmissionsPopup 
                order={showPricePopup} 
                userMap={userMap} 
                staffList={staffList}
                onClose={() => setShowPricePopup(null)}
            />
        )}
      </div>
    </div>
  )
}
